/** Sizes subpage — category ordering and size-label resolution. */

import type {
  CategorySizeData,
  SizeCategoryKey,
  SurveyResults,
} from '@/types/sizes'
import { primarySize } from '@/features/sizes/suggested-size'

export const FAIR_PROFILE_V2_SIZE_CATEGORIES: SizeCategoryKey[] = [
  'tops',
  'bottoms',
  'dresses',
  'outerwear',
  'lingerie',
  'shoes',
]

export interface FairProfileV2SizeCategory {
  key: SizeCategoryKey
  data: CategorySizeData
  /** Recommended label, when the API returned one. */
  recommended: string | null
}

/** Only the categories the survey actually answered, in a fixed order. */
export function buildFairProfileV2SizeCategories(
  results: SurveyResults | null | undefined,
): FairProfileV2SizeCategory[] {
  if (!results) {
    return []
  }

  return FAIR_PROFILE_V2_SIZE_CATEGORIES.filter(
    (key) => results[key] != null,
  ).map((key) => {
    const data = results[key] as CategorySizeData
    return {
      key,
      data,
      recommended: primarySize(data.suggestedSize) ?? null,
    }
  })
}

/** User pick wins, then the recommendation, then whatever is first. */
export function resolveFairProfileV2SelectedSize(
  data: CategorySizeData,
  recommended: string | null,
): string {
  const userSelected = data.userSelectedSize
  if (typeof userSelected === 'string') {
    return userSelected
  }
  if (userSelected && typeof userSelected === 'object') {
    const label = primarySize(userSelected)
    if (label) return label
  }

  return recommended ?? data.sizes[0] ?? ''
}

/** The badge only earns its place when the pick differs from the advice. */
export function shouldShowFairProfileV2Recommendation(
  selected: string,
  recommended: string | null,
): boolean {
  return Boolean(recommended) && Boolean(selected) && selected !== recommended
}

/* ── Size rows of the redesign (`Sizes · components` 03 / 08) ─────────── */

/** API measurement types each category is matched from. */
export const FAIR_PROFILE_V2_SIZE_NEEDS: Record<SizeCategoryKey, string[]> = {
  tops: ['chest'],
  bottoms: ['waist', 'hip'],
  dresses: ['chest', 'waist', 'hip'],
  outerwear: ['chest'],
  lingerie: ['underChest', 'hip'],
  shoes: ['footLength'],
}

export type FairProfileV2SizeHint =
  /** "Add under-bust and hips to match your size" → scrolls to the first missing field. */
  | { kind: 'add'; missing: string[] }
  /** "Your measurements suggest M · Use" → applies the suggestion. */
  | { kind: 'use'; size: string }
  | null

export interface FairProfileV2SizeRowState {
  /** What the dropdown shows; null is the "Select" placeholder. */
  value: string | null
  suggested: string | null
  hint: FairProfileV2SizeHint
}

/**
 * The row of one category. A manual pick wins; otherwise the size matched from
 * the measurements; with neither the row asks for the missing measurements.
 * A manual pick that differs from the match offers the match ("Use").
 */
export function resolveFairProfileV2SizeRow(
  key: SizeCategoryKey,
  data: CategorySizeData | null | undefined,
  filledTypes: ReadonlySet<string>,
): FairProfileV2SizeRowState {
  const suggested = primarySize(data?.suggestedSize) ?? null
  const userSelected = data?.userSelectedSize
  const picked =
    typeof userSelected === 'string'
      ? userSelected || null
      : userSelected
        ? primarySize(userSelected)
        : null
  const value = picked ?? suggested

  if (picked && suggested && picked !== suggested) {
    return { value, suggested, hint: { kind: 'use', size: suggested } }
  }
  if (!value) {
    const missing = FAIR_PROFILE_V2_SIZE_NEEDS[key].filter((type) => !filledTypes.has(type))
    return { value, suggested, hint: missing.length ? { kind: 'add', missing } : null }
  }
  return { value, suggested, hint: null }
}

/* ── Categories per body (`Sizes / Desktop / Full`, `Full, men`) ───────── */

/** Men have no dresses; the rest is the same list in the same order. */
export const FAIR_PROFILE_V2_BODY_CATEGORIES: Record<'feminine' | 'masculine', SizeCategoryKey[]> = {
  feminine: ['tops', 'bottoms', 'dresses', 'outerwear', 'lingerie', 'shoes'],
  masculine: ['tops', 'bottoms', 'outerwear', 'lingerie', 'shoes'],
}

const LETTER_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL']

function range(from: number, to: number): string[] {
  return Array.from({ length: to - from + 1 }, (_, index) => String(from + index))
}

/**
 * The size list of a row. The survey results carry it once the survey has
 * run; before that (`/survey/results` is `{}`) the row still offers the
 * international scale so a size can be picked by hand (`Empty` state).
 */
export function fairProfileV2SizeOptions(
  key: SizeCategoryKey,
  body: 'feminine' | 'masculine',
  data: CategorySizeData | null | undefined,
): string[] {
  if (data?.sizes?.length) return data.sizes
  if (key === 'shoes') return body === 'feminine' ? range(35, 42) : range(39, 47)
  return LETTER_SIZES
}
